import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Link,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
  Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import useAPICall from '../../hooks/useAPICall';
import { API_CONFIG } from '../../config/api.config';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';
import {
  MRT_GlobalFilterTextField as MrtGlobalFilterTextField,
  MRT_ShowHideColumnsButton as MrtShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as MrtToggleDensePaddingButton,
  MRT_ToggleFiltersButton as MrtToggleFiltersButton,
  MRT_ToggleGlobalFilterButton as MrtToggleGlobalFilterButton,
  MRT_FullScreenToggleButton as MrtFullScreenToggleButton,
} from 'material-react-table';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';
import { commonTableStyles } from '../../components/common/tableStyles';

const asArray = (value) => (Array.isArray(value) ? value : []);

const formatTableData = (state, ref) => {
  if (!state) return [];
  if (Array.isArray(state)) return state;
  if (typeof state !== 'object') return [];

  const array = [];
  Object.keys(state).forEach((i) => {
    if (ref === 'isn') {
      Object.keys(state[i] || {}).forEach((j) => {
        const isn = { ...(state[i]?.[j] || {}) };
        isn.Id = j;
        isn.isnCode = i;
        array.push(isn);
      });
    } else if (ref === 'sln') {
      const sln = { ...(state[i] || {}) };
      sln.slnAuthNbr = i;
      array.push(sln);
    } else if (ref === 'spc') {
      const spc = { ...(state[i] || {}) };
      spc.rank = i;
      array.push(spc);
    }
  });

  return array;
};

const normalizeSources = (data) => {
  const sourceRecords = data?.hcpSourceRecords || data?.sourceRecords || data?.srcRecord || {};
  if (!sourceRecords || typeof sourceRecords !== 'object') return [];
  return Object.entries(sourceRecords).map(([source, value]) => {
    const normalized = value && typeof value === 'object' ? { ...value } : { value };
    normalized.source = source;
    if (normalized.isn) normalized.isn = formatTableData(normalized.isn, 'isn');
    if (normalized.sln) normalized.sln = formatTableData(normalized.sln, 'sln');
    if (normalized.spcl) normalized.spcl = formatTableData(normalized.spcl, 'spc');
    if (normalized.subSpcl) normalized.subSpcl = formatTableData(normalized.subSpcl, 'spc');
    return normalized;
  });
};

const formatSpecialty = (value) => {
  if (!value) return '-';
  if (Array.isArray(value)) return value.map((v) => v?.spclDesc || v?.spclDescNm || v?.spclCode).filter(Boolean).join(', ') || '-';
  if (typeof value === 'object') {
    return Object.values(value)
      .map((v) => v?.spclDesc || v?.spclDescNm || v?.spclCode)
      .filter(Boolean)
      .join(', ') || '-';
  }
  return String(value);
};

const TabPanel = ({ value, index, children, sx = {} }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 0, ...sx }}>{children}</Box>}
  </div>
);

TabPanel.propTypes = {
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  children: PropTypes.node,
};

TabPanel.defaultProps = {
  children: null,
};

const SourceRecordsPageLoader = () => (
  <Paper elevation={0} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
    <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e2e8f0', backgroundColor: '#fff' }}>
      <Skeleton variant="text" width={220} height={36} />
      <Skeleton variant="text" width={280} height={22} />
    </Box>

    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} variant="rounded" width={100} height={42} sx={{ borderRadius: '8px' }} />
          ))}
        </Box>
        <Skeleton variant="rounded" width={240} height={42} sx={{ borderRadius: '8px' }} />
      </Box>

      <Skeleton variant="rounded" height={400} sx={{ borderRadius: '12px' }} />
    </Box>
  </Paper>
);

const HCPSrcRecords = () => {
  const navigate = useNavigate();
  const { hcpId } = useParams();
  const { loading, error, data, execute } = useAPICall();
  const [selectedSources, setSelectedSources] = useState([]);
  const [tab, setTab] = useState(0);
  const [paginationByTable, setPaginationByTable] = useState({
    basic: { page: 1, pageSize: 10 },
    address: { page: 1, pageSize: 10 },
    specialty: { page: 1, pageSize: 10 },
    subSpecialty: { page: 1, pageSize: 10 },
    sln: { page: 1, pageSize: 10 },
    isn: { page: 1, pageSize: 10 },
  });

  const response = useMemo(() => data?.data || data || {}, [data]);
  const srcRecords = useMemo(() => normalizeSources(response), [response]);

  const sources = useMemo(() => srcRecords.map((item) => item.source).filter(Boolean), [srcRecords]);

  useEffect(() => {
    const load = async () => {
      try {
        await execute(API_CONFIG.hcp.details, null, {
          params: { mdmId: hcpId },
          errorMessage: 'Unable to load HCP source records.',
          retries: 1,
        });
      } catch {
      }
    };
    load();
  }, [execute, hcpId]);

  useEffect(() => {
    if (sources.length > 0 && selectedSources.length === 0) {
      setSelectedSources([sources[0]]);
    }
  }, [selectedSources.length, sources]);

  const onSourceFilterChange = useCallback((source) => (event) => {
    const checked = event.target.checked;
    setSelectedSources((prev) => {
      if (checked) {
        return prev.includes(source) ? prev : [...prev, source];
      }
      return prev.filter((item) => item !== source);
    });
  }, []);

  const selectedSourceRecords = useMemo(
    () => srcRecords.filter((item) => selectedSources.includes(item.source)),
    [selectedSources, srcRecords]
  );

  const basicRows = useMemo(() => selectedSourceRecords, [selectedSourceRecords]);

  const addressRows = useMemo(() => {
    const rows = [];
    selectedSourceRecords.forEach((item) => {
      const addressEntries = item.address && typeof item.address === 'object'
        ? Object.entries(item.address)
        : [];
      addressEntries.forEach(([, value]) => {
        if (value && typeof value === 'object') {
          rows.push({ source: item.source, ...value });
        }
      });
    });
    return rows;
  }, [selectedSourceRecords]);

  const specialtyRows = useMemo(
    () => selectedSourceRecords.flatMap((item) => asArray(item.spcl).map((spc) => ({ source: item.source, ...spc }))),
    [selectedSourceRecords]
  );

  const subSpecialtyRows = useMemo(
    () => selectedSourceRecords.flatMap((item) => asArray(item.subSpcl).map((spc) => ({ source: item.source, ...spc }))),
    [selectedSourceRecords]
  );

  const slnRows = useMemo(
    () => selectedSourceRecords.flatMap((item) => asArray(item.sln).map((sln) => ({ source: item.source, ...sln }))),
    [selectedSourceRecords]
  );

  const isnRows = useMemo(
    () => selectedSourceRecords.flatMap((item) => asArray(item.isn).map((isn) => ({ source: item.source, ...isn }))),
    [selectedSourceRecords]
  );

  const tableInitialState = useMemo(() => ({
    showGlobalFilter: false,
    showColumnFilters: false,
  }), []);

  const setTablePage = useCallback((tableKey, nextPage) => {
    setPaginationByTable((prev) => ({
      ...prev,
      [tableKey]: {
        ...prev[tableKey],
        page: nextPage,
      },
    }));
  }, []);

  const setTablePageSize = useCallback((tableKey, nextPageSize) => {
    setPaginationByTable((prev) => ({
      ...prev,
      [tableKey]: {
        ...prev[tableKey],
        pageSize: nextPageSize,
        page: 1,
      },
    }));
  }, []);

  const sharedTableProps = useMemo(() => ({
    ...commonTableStyles
  }), []);

  const renderTableToolbar = useCallback(({ table }) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{ 
            style: { 
              backgroundColor: '#1E88E5',
              borderTopLeftRadius: '2px',
              borderTopRightRadius: '2px',
            } 
          }}
           sx={{
                minHeight: 48,
                '& .MuiTabs-indicator': {
                  backgroundColor: '#1E88E5',
                  height: 2.5,
                  borderTopLeftRadius: 3,
                  borderTopRightRadius: 3,
                },
                '& .MuiTabs-flexContainer': {
                  gap: 0.2,
                },
              }}
        >
          {['Basic Details', 'Address', 'Specialty', 'SLN', 'ISN'].map((label, index) => (
            <Tab 
              key={label}
              label={label} 
              disableRipple 
                sx={{
                      minHeight: 48,
                      px: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      
                      color: '#64748b',
                      '&.Mui-selected': { color: '#1E88E5' },
                    }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Source Filters Bar and Toolbar */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          borderTop: "1px solid #e2e8f0",
          background: "#ffffff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: "#0f172a",
              
              mr: 1,
            }}
          >
            Source Filters :
          </Typography>

          {sources.map((source) => {
            const isSelected = selectedSources.includes(source);

            return (
              <Box
                key={source}
                onClick={() => {
                  setSelectedSources((prev) =>
                    isSelected
                      ? prev.filter((s) => s !== source)
                      : [...prev, source]
                  );
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.8,
                  px: 1.4,
                  py: 0.7,
                  borderRadius: "10px",
                  cursor: "pointer",
                }}
              >
                <Checkbox
                  checked={isSelected}
                  size="small"
                  sx={{
                      padding: 0,
                      color: "#cbd5e1", // lighter border
                      "& .MuiSvgIcon-root": {
                        
                      },
                      "&.Mui-checked": {
                        color: "#1E88E5",
                      },
                    }}
                />

                <Typography
                  sx={{
                    
                    fontWeight: 600,
                    color: isSelected ? "#1E3A8A" : "#334155",
                    lineHeight: 1,
                  }}
                >
                  {source}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ minWidth: table.getState().showGlobalFilter ? 240 : 0, transition: 'all 0.2s ease-in-out' }}>
            {table.getState().showGlobalFilter ? (
              <MrtGlobalFilterTextField 
                table={table} 
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    borderColor: '#e2e8f0',
                    '& fieldset': { borderColor: '#e2e8f0' },
                    '&:hover fieldset': { borderColor: '#cbd5e1' },
                    '&.Mui-focused fieldset': { borderColor: '#1E88E5' }
                  }
                }} 
              />
            ) : null}
          </Box>
          <MrtToggleGlobalFilterButton table={table} sx={{ color: '#64748b' }} />
          <MrtToggleFiltersButton table={table} sx={{ color: '#64748b' }} />
          <MrtShowHideColumnsButton table={table} sx={{ color: '#64748b' }} />
          <MrtToggleDensePaddingButton table={table} sx={{ color: '#64748b' }} />
          <MrtFullScreenToggleButton table={table} sx={{ color: '#64748b' }} />
        </Box>
      </Box>
    </Box>
  ), [selectedSources, sources, tab]);

  return (
    <Box>
      <Box sx={{ mb: 1 }}>
        <PageBreadcrumbs items={[
          { label: 'HCP Search', path: '/search/hcp' },
          { label: 'HCP Details', path: `/details/hcp/${hcpId}` },
          { label: 'HCP Source Records' }
        ]} />
      </Box>


      {loading ? (
        <SourceRecordsPageLoader />
      ) : (
        <Paper elevation={0} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', backgroundColor: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
            <Box sx={{ p: 0 }}>
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: '1px solid #E5E7EB',
                  borderLeft: '4px solid #1E88E5',
                  display: 'flex',
                  background:"#fbfdfe",
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E293B',  mb: 0.25, lineHeight: 1.2 }}>
                    HCP Source Records
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b',  fontWeight: 500 }}>
                    Source-system records for HCP ID: <Box component="span" sx={{ color: '#1E88E5', fontWeight: 600 }}>{hcpId}</Box>
                  </Typography>
                </Box>
                <Chip 
                  label={`${srcRecords.length} Sources Found`} 
                  size="small" 
                  sx={{ 
                    height: 24, 
                    px: 0.5, 
                    backgroundColor: alpha('#1E88E5', 0.08), 
                    color: '#1E88E5', 
                    fontWeight: 800,
                    
                    borderRadius: '6px',
                    border: '1px solid',
                    borderColor: alpha('#1E88E5', 0.15)
                  }} 
                />
              </Box>
            </Box>

          <Box sx={{ p: 0, pt: 0 }}>
            <TabPanel value={tab} index={0}>
              <GlobalMaterialTable
                columns={[
                  { header: 'Source Name', id: 'source', accessorFn: (row) => row.source || '-' },
                  { header: 'External Id', id: 'externalId', accessorFn: (row) => row.externalId || '-' },
                  { header: 'First Name', id: 'firstName', accessorFn: (row) => row.firstName || '-' },
                  { header: 'Middle Name', id: 'middleName', accessorFn: (row) => row.middleName || '-' },
                  { header: 'Last Name', id: 'lastName', accessorFn: (row) => row.lastName || '-' },
                  { header: 'Suffix', id: 'suffix', accessorFn: (row) => row.suffix || '-' },
                  { header: 'Birth Date', id: 'birthDate', accessorFn: (row) => row.birthDate || '-' },
                  { header: 'Gender', id: 'gender', accessorFn: (row) => row.gender || '-' },
                  { header: 'Degree', id: 'degree', accessorFn: (row) => row.degree || '-' },
                  { header: 'Graduation Year', id: 'graduationYear', accessorFn: (row) => row.graduationYear || '-' },
                  { header: 'Medical School', id: 'medicalSchool', accessorFn: (row) => row.medSchoolDesc || '-' },
                  { header: 'Specialty', id: 'specialty', accessorFn: (row) => formatSpecialty(row.spcl) },
                  { header: 'Phone', id: 'phone', accessorFn: (row) => row.cellPhone || '-' },
                  { header: 'Updated Date', id: 'updatedDate', accessorFn: (row) => row.updatedDate || '-' },
                ]}
                data={basicRows}
                emptyMessage="No source records found"
                enablePagination={true}
                enableTopToolbar={true}
                enableBottomToolbar={true}
                initialState={tableInitialState}
                autoResetPageIndex={false}
                renderTopToolbar={renderTableToolbar}
                page={paginationByTable.basic.page}
                pageSize={paginationByTable.basic.pageSize}
                onPageChange={(nextPage) => setTablePage('basic', nextPage)}
                onPageSizeChange={(nextPageSize) => setTablePageSize('basic', nextPageSize)}
                rowCount={basicRows.length}
                manualPagination={false}
                layoutMode="grid"
                OuterBorder={"none"}
                // {...sharedTableProps}
              />
            </TabPanel>

            <TabPanel value={tab} index={1}>
              <GlobalMaterialTable
                columns={[
                  { header: 'Source', id: 'source', accessorFn: (row) => row.source || '-' },
                  { header: 'Address ID', id: 'addressId', accessorFn: (row) => row.onekeyAddrId || row.addrId || '-' },
                  { header: 'Address Line 1', id: 'addr1', accessorFn: (row) => row.addrLn1 || row.addressLine1 || row.address1 || '-' },
                  { header: 'Address Line 2', id: 'addr2', accessorFn: (row) => row.addrLn2 || row.addressLine2 || '-' },
                  { header: 'City', id: 'city', accessorFn: (row) => row.cityNm || row.city || '-' },
                  { header: 'State', id: 'state', accessorFn: (row) => row.stCd || row.state || '-' },
                  { header: 'Zip4', id: 'zip4', accessorFn: (row) => row.zip4 || '-' },
                  { header: 'Zip5', id: 'zip5', accessorFn: (row) => row.zip5 || '-' },
                  { header: 'Status', id: 'status', accessorFn: (row) => row.addressStatusLabel || row.statusLabel || '-' },
                  { header: 'Client Preferred', id: 'clientPreferred', accessorFn: (row) => row.isClientPreferred || row.clientPreferred || '-' },
                  { header: 'Telephone', id: 'telephone', accessorFn: (row) => row.addressTelephone || row.telephone || '-' },
                  { header: 'Cell Phone', id: 'cellPhone', accessorFn: (row) => row.addressCellPhone || row.cellPhone || '-' },
                  { header: 'Is Sampleable', id: 'isSampleable', accessorFn: (row) => row.isSampleable || '-' },
                ]}
                data={addressRows}
                emptyMessage="No address details found"
                enablePagination={true}
                enableTopToolbar={true}
                enableBottomToolbar={true}
                initialState={tableInitialState}
                autoResetPageIndex={false}
                renderTopToolbar={renderTableToolbar}
                page={paginationByTable.address.page}
                pageSize={paginationByTable.address.pageSize}
                onPageChange={(nextPage) => setTablePage('address', nextPage)}
                onPageSizeChange={(nextPageSize) => setTablePageSize('address', nextPageSize)}
                rowCount={addressRows.length}
                manualPagination={false}
                layoutMode="grid"
                  OuterBorder={"none"}
                // {...sharedTableProps}
              />
            </TabPanel>

            <TabPanel value={tab} index={2}>
              <GlobalMaterialTable
                columns={[
                  { header: 'Source', id: 'source', accessorFn: (row) => row.source || '-' },
                  { header: 'Specialty Code', id: 'spclCode', accessorFn: (row) => row.spclCode || '-' },
                  { header: 'Specialty Description', id: 'spclDesc', accessorFn: (row) => row.spclDesc || '-' },
                  { header: 'Profession Code', id: 'prfsnCode', accessorFn: (row) => row.prfsnCode || '-' },
                  { header: 'Profession Description', id: 'prfsnDesc', accessorFn: (row) => row.prfsnDesc || '-' },
                  { header: 'Specialty Group', id: 'spclGroup', accessorFn: (row) => row.clientSpclGroup || '-' },
                  { header: 'Excluded Specialty', id: 'excludedSpcl', accessorFn: (row) => row.excludedSpcl || '-' },
                  { header: 'Primary', id: 'primary', accessorFn: (row) => row.primary || '-' },
                ]}
                data={specialtyRows}
                emptyMessage="No specialty details found"
                enablePagination={true}
                enableTopToolbar={true}
                enableBottomToolbar={true}
                initialState={tableInitialState}
                autoResetPageIndex={false}
                renderTopToolbar={renderTableToolbar}
                page={paginationByTable.specialty.page}
                pageSize={paginationByTable.specialty.pageSize}
                onPageChange={(nextPage) => setTablePage('specialty', nextPage)}
                onPageSizeChange={(nextPageSize) => setTablePageSize('specialty', nextPageSize)}
                rowCount={specialtyRows.length}
                manualPagination={false}
                layoutMode="grid"
                  OuterBorder={"none"}
                    OuterBoxShadow={"none"}
                // {...sharedTableProps}
              />

              <Divider sx={{ mb: 0, mx: 0, borderColor: '#e2e8f0' }} />

              <Box sx={{ p:0, mt: 0 }}>
              <GlobalMaterialTable
                columns={[
                  { header: 'Source', id: 'source', accessorFn: (row) => row.source || '-' },
                  { header: 'Specialty Code', id: 'spclCd', accessorFn: (row) => row.spclCd || '-' },
                  { header: 'Specialty Description', id: 'spclDesc', accessorFn: (row) => row.spclDesc || '-' },
                  { header: 'Subspecialty Code', id: 'subSpclCode', accessorFn: (row) => row.subSpclCode || '-' },
                  { header: 'Subspecialty Description', id: 'subSpclDesc', accessorFn: (row) => row.subSpclDesc || '-' },
                  { header: 'Primary', id: 'primary', accessorFn: (row) => row.primary || '-' },
                  { header: 'Excluded Subspecialty', id: 'excludedSubSpcl', accessorFn: (row) => row.excludedSubSpcl || '-' },
                ]}
                data={subSpecialtyRows}
                emptyMessage="No subspecialty details found"
                enablePagination={true}
                enableTopToolbar={true}
                enableBottomToolbar={true}
                initialState={tableInitialState}
                autoResetPageIndex={false}
                page={paginationByTable.subSpecialty.page}
                pageSize={paginationByTable.subSpecialty.pageSize}
                onPageChange={(nextPage) => setTablePage('subSpecialty', nextPage)}
                onPageSizeChange={(nextPageSize) => setTablePageSize('subSpecialty', nextPageSize)}
                rowCount={subSpecialtyRows.length}
                manualPagination={false}
                layoutMode="grid"
                OuterBorder={"none"}
                OuterBoxShadow={"none"}
                renderTopToolbarCustomActions={() => (
                  <Box sx={{ ml: 1 }}>
                    <Typography variant="subtitle1" sx={{ color: '#0f172a', fontWeight: 700, lineHeight: 1.2 }}>
                      Subspecialty
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 400 }}>
                      Specialty classification
                    </Typography>
                  </Box>
                )}
                // {...sharedTableProps}
              />
              </Box>
            </TabPanel>

            <TabPanel value={tab} index={3}>
              <GlobalMaterialTable
                columns={[
                  { header: 'Source', id: 'source', accessorFn: (row) => row.source || '-' },
                  { header: 'Auth Number', id: 'authNumber', accessorFn: (row) => row.slnAuthNbr || '-' },
                  { header: 'Status', id: 'status', accessorFn: (row) => row.authnStatDesc || '-' },
                  { header: 'State Code', id: 'stateCode', accessorFn: (row) => row.stCd || '-' },
                  { header: 'Board ID', id: 'boardId', accessorFn: (row) => row.stBrdId || '-' },
                  { header: 'Board Name', id: 'boardName', accessorFn: (row) => row.stBrdNm || '-' },
                  { header: 'Type ID', id: 'typeId', accessorFn: (row) => row.stLicTypId || '-' },
                  { header: 'Type Name', id: 'typeName', accessorFn: (row) => row.stLicTypNm || '-' },
                  { header: 'Privilege ID', id: 'privilegeId', accessorFn: (row) => row.stLicPrivlId || '-' },
                  { header: 'Privilege', id: 'privilege', accessorFn: (row) => row.stLicPrivlNm || '-' },
                  { header: 'Certification Name', id: 'certName', accessorFn: (row) => row.stLicCertNm || '-' },
                  { header: 'Privilege Rank', id: 'privilegeRank', accessorFn: (row) => row.stLicTypPrivlRnkNbr || '-' },
                  { header: 'Effective Date', id: 'effDate', accessorFn: (row) => row.authnEffDt || '-' },
                  { header: 'Expiration Date', id: 'expDate', accessorFn: (row) => row.authnTrmtnDt || '-' },
                  { header: 'Grace Period', id: 'gracePeriod', accessorFn: (row) => row.gracePeriod || '-' },
                  { header: 'Key', id: 'key', accessorFn: (row) => row.slnKeyNbr || '-' },
                  { header: 'Status Updated Date', id: 'statusUpdated', accessorFn: (row) => row.authnStatDt || '-' },
                  { header: 'Deactivation Reason', id: 'deactReason', accessorFn: (row) => row.stLicDeactvtnRsnDesc || '-' },
                ]}
                data={slnRows}
                emptyMessage="No SLN details found"
                enablePagination={true}
                enableTopToolbar={true}
                enableBottomToolbar={true}
                initialState={tableInitialState}
                autoResetPageIndex={false}
                renderTopToolbar={renderTableToolbar}
                page={paginationByTable.sln.page}
                pageSize={paginationByTable.sln.pageSize}
                onPageChange={(nextPage) => setTablePage('sln', nextPage)}
                onPageSizeChange={(nextPageSize) => setTablePageSize('sln', nextPageSize)}
                rowCount={slnRows.length}
                manualPagination={false}
                layoutMode="grid"
                  OuterBorder={"none"}
                // {...sharedTableProps}
              />
            </TabPanel>

            <TabPanel value={tab} index={4}>
              <GlobalMaterialTable
                columns={[
                  { header: 'Source', id: 'source', accessorFn: (row) => row.source || '-' },
                  { header: 'Code', id: 'code', accessorFn: (row) => row.isnCode || '-' },
                  { header: 'Id', id: 'id', accessorFn: (row) => row.Id || '-' },
                  { header: 'Status', id: 'status', accessorFn: (row) => row.status || '-' },
                  { header: 'Effective Date', id: 'effDate', accessorFn: (row) => row.effDate || '-' },
                  { header: 'Termination Date', id: 'termDate', accessorFn: (row) => row.termnDate || '-' },
                  { header: 'Deactivation Reason', id: 'deactReason', accessorFn: (row) => row.deactvtnRsn || '-' },
                ]}
                data={isnRows}
                emptyMessage="No ISN details found"
                enablePagination={true}
                enableTopToolbar={true}
                enableBottomToolbar={true}
                initialState={tableInitialState}
                autoResetPageIndex={false}
                renderTopToolbar={renderTableToolbar}
                page={paginationByTable.isn.page}
                pageSize={paginationByTable.isn.pageSize}
                onPageChange={(nextPage) => setTablePage('isn', nextPage)}
                onPageSizeChange={(nextPageSize) => setTablePageSize('isn', nextPageSize)}
                rowCount={isnRows.length}
                manualPagination={false}
                layoutMode="grid"
                  OuterBorder={"none"}
                // {...sharedTableProps}
              />
            </TabPanel>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default HCPSrcRecords;
